import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Copy, Check, Play, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';
import { Player, Room } from '../types';

interface RoomPageProps {
  room: Room;
  player: Player;
  onBack: () => void;
  onStartCategoryVoting: () => void;
}

export const RoomPage: React.FC<RoomPageProps> = ({
  room,
  player,
  onBack,
  onStartCategoryVoting,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  console.log('RoomPage rendering with:', { room, player });

  const copyRoomCode = () => {
    navigator.clipboard.writeText(room.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canStartGame = room.players.length >= 3 && room.players.length <= 10;
  const isHost = player.isHost === true;
  console.log('Can start game:', canStartGame, 'Players count:', room.players.length);
  console.log('Player data:', player);
  console.log('Is host:', isHost, 'player.isHost:', player.isHost);
  console.log('Room players:', room.players);

  const handleStartCategoryVotingClick = () => {
    console.log('Start category voting button clicked!');
    console.log('onStartCategoryVoting function:', onStartCategoryVoting);
    onStartCategoryVoting();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20 pointer-events-none"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            {t('room.title')}
          </h1>
          {!room.isLocal && (
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-xl text-white/80">
                {t('room.roomCode', { code: room.code })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={copyRoomCode}
                icon={copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              >
                {copied ? t('common.copied') : t('common.copy')}
              </Button>
            </div>
          )}
        </div>

        {/* Players Section */}
        <Card className="mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-6 h-6 text-white" />
            <h2 className="text-2xl font-bold text-white">
              {t('room.players', { count: room.players.length })}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {room.players.map((p) => (
              <div
                key={p.id}
                className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                  p.id === player.id
                    ? 'bg-primary-500/20 border-primary-400'
                    : 'bg-white/5 border-white/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                    p.id === player.id
                      ? 'bg-primary-500'
                      : 'bg-white/20'
                  }`}>
                    {p.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-semibold">
                      {p.name}
                      {p.isHost && (
                        <span className="ml-2 text-xs bg-primary-500 px-2 py-1 rounded">
                          👑 Anfitrión
                        </span>
                      )}
                    </p>
                    <p className={`text-sm ${
                      p.isConnected ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {p.isConnected ? 'Conectado' : 'Desconectado'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {room.players.length < 3 && (
            <div className="text-center p-4 bg-warning-500/20 border border-warning-400 rounded-xl">
              <p className="text-warning-400 font-medium">
                Se necesitan mínimo 3 jugadores para comenzar
              </p>
            </div>
          )}

          {room.players.length > 10 && (
            <div className="text-center p-4 bg-accent-500/20 border border-accent-400 rounded-xl">
              <p className="text-accent-400 font-medium">
                Máximo 10 jugadores permitidos
              </p>
            </div>
          )}
        </Card>

        {/* Start Category Voting Button */}
        <div className="text-center">
          {isHost ? (
            <>
              <Button
                variant="primary"
                size="xl"
                onClick={handleStartCategoryVotingClick}
                disabled={!canStartGame}
                icon={<Play className="w-6 h-6" />}
                className="px-12 py-6 text-xl"
              >
                Iniciar Votación de Categoría
              </Button>
              
              {!canStartGame && (
                <p className="text-white/60 mt-4">
                  {room.players.length < 3 ? 'Esperando más jugadores...' : 'Demasiados jugadores'}
                </p>
              )}
            </>
          ) : (
            <p className="text-white/80 text-lg">
              Esperando a que el anfitrión inicie la votación...
            </p>
          )}
        </div>

        {/* Game Info */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card variant="glass" className="text-center">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h4 className="text-white font-semibold mb-1">Jugadores</h4>
            <p className="text-white/60 text-sm">{room.players.length}/10</p>
          </Card>
          
          <Card variant="glass" className="text-center">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-lg">🎯</span>
            </div>
            <h4 className="text-white font-semibold mb-1">Impostores</h4>
            <p className="text-white/60 text-sm">1-2 (según jugadores)</p>
          </Card>
          
          <Card variant="glass" className="text-center">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-lg">🌍</span>
            </div>
            <h4 className="text-white font-semibold mb-1">Idioma</h4>
            <p className="text-white/60 text-sm">{room.language === 'es' ? 'Español' : 'English'}</p>
          </Card>
        </div>
      </div>
    </div>
  );
};
